import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { extractPriceListDummyData } from "./priceListDummyService";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
} from "../../../utils/documentUploadTypes";

const PriceListUploadPage = ({ onBack, onSave }) => {
  const priceListInputRef = useRef(null);
  const filterInputRef = useRef(null);

  const [priceListFile, setPriceListFile] = useState(null);
  const [filterFile, setFilterFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPriceListPreview, setShowPriceListPreview] = useState(false);
  const [showFilterPreview, setShowFilterPreview] = useState(false);

  const handleDrop = (e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }
    setExtractedData(null);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === "priceList") {
      setPriceListFile(file);
      setShowPriceListPreview(false);
    } else {
      setFilterFile(file);
      setShowFilterPreview(false);
    }
    setExtractedData(null);
  };

  const handleClearFile = (type) => {
    if (type === "priceList") {
      setPriceListFile(null);
      setShowPriceListPreview(false);
      if (priceListInputRef.current) priceListInputRef.current.value = "";
    } else {
      setFilterFile(null);
      setShowFilterPreview(false);
      if (filterInputRef.current) filterInputRef.current.value = "";
    }
    setExtractedData(null);
  };

  const handleExtract = async () => {
    if (!priceListFile || !filterFile) return;
    try {
      setLoading(true);
      const result = await extractPriceListDummyData();
      setExtractedData(result);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!extractedData) return;
    onSave(extractedData);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Create Quotation" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Upload Price List
          </Typography>

          <Box
            onDrop={(e) => handleDrop(e, "priceList")}
            onDragOver={handleDragOver}
            sx={{
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {priceListFile ? priceListFile.name : "Drag and drop file here"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => priceListInputRef.current?.click()}
              >
                Browse
              </Button>

              {priceListFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowPriceListPreview((prev) => !prev)}
                  >
                    {showPriceListPreview ? "Hide Preview" : "Preview"}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => handleClearFile("priceList")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Filter Record
          </Typography>

          <Box
            onDrop={(e) => handleDrop(e, "filter")}
            onDragOver={handleDragOver}
            sx={{
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {filterFile ? filterFile.name : "Drag and drop file here"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => filterInputRef.current?.click()}
              >
                Browse
              </Button>

              {filterFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowFilterPreview((prev) => !prev)}
                  >
                    {showFilterPreview ? "Hide Preview" : "Preview"}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => handleClearFile("filter")}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <input
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        ref={priceListInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "priceList")}
      />
      <input
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        ref={filterInputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e, "filter")}
      />

      {showPriceListPreview && priceListFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            Price List Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(priceListFile)}
            title="Price List Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !priceListFile || !filterFile || loading ? 0.7 : 1,
          }}
          onClick={handleExtract}
          disabled={!priceListFile || !filterFile || loading}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !extractedData || loading ? 0.7 : 1,
          }}
          onClick={handleSave}
          disabled={!extractedData || loading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default PriceListUploadPage;
